import react, { useState, useEffect } from 'react';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../Styles/User/loginStyle.css';

function Login() {
    const [currentView, setCurrentView] = useState('login');
    const [stage, setStage] = useState(1);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [postCode, setPostCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [communication, setCommunication] = useState(false);
    const [error, setError] = useState(null);
    const [dob, setDob] = useState('');

    const auth = getAuth();
    const navigate = useNavigate();

    const [mfaCode, setMfaCode] = useState('');
    const [isMfaRequired, setIsMfaRequired] = useState(false);

    // Function to handle switching between login and sign up views
    const handleSwitchView = (event) => {
        event.preventDefault();
        setCurrentView(currentView === 'login' ? 'signUp' : 'login');
    };

    const handleNext = () => {
        if (password !== confPassword) {
            setError("Passwords do not match!");
            return;
        }
        if (password.length < 8) {
            setError("Password should be at least 8 characters");
            return;
        }
        setError(null);
        setStage(2);
    };

    const getFriendlyError = (code) => {
        switch (code) {
            case "auth/user-not-found":
                return "No account found with that email.";
            case "auth/wrong-password":
            case "auth/invalid-credential":
                return "Incorrect email or password.";
            case "auth/too-many-requests":
                return "Too many login attempts. Please wait and try again.";
            case "auth/invalid-email":
                return "The email address is not valid.";
            case "auth/internal-error":
                return "An internal error occurred. Please try again.";
            case "auth/network-request-failed":
                return "Network error. Please check your internet connection.";
            default:
                return "An unexpected error occurred. Please try again.";
        }
    };  

    const validatePassword = () => {
        if (password !== confPassword) {
            setError("Passwords do not match!");
            return;
        };

        if (password.length < 8) {
            setError("Password should be atleast 6 characters");
            return;
        };
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            navigate('/home');
        } catch (error) {
            const code = error?.code || (error?.message?.includes("auth/") ? error.message.match(/auth\/[a-zA-Z\-]+/)?.[0] : null);
            const message = getFriendlyError(code);
            setError(message);
        }
    };

    const handleMfaValidation = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('/validate-mfa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: mfaCode }),
            });
            if (response.ok) {
                console.log('MFA validation successful');
                // Proceed to the dashboard or next step
            } else {
                setError('Invalid MFA code');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleForgotPassword = (e) => {
        e.preventDefault();
        setCurrentView('forgotPassword');
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            await sendPasswordResetEmail(auth, email);
            alert("Password reset email sent!");
            setCurrentView('login');  // Redirect to login after email is sent
        } catch (error) {
            setError("Error sending password reset email.");
        }
    };

    const validatePhoneNumber = (phone) => {
        const phoneRegex = /^(\+?\d{1,3})?[-.\s]?(\(?\d{3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4,6}$/;
        return phoneRegex.test(phone);
    };

    const validatePostCode = (code) => {
        const postcodeRegex = /^\d{4,10}$/;
        return postcodeRegex.test(code);
    };    

    const validateAddress = (address) => {
        return address.trim().length >= 5 && /[a-zA-Z]/.test(address);
    };    

    const handleSignUp = async (e) => {
        e.preventDefault();
        if (password !== confPassword) { 
            setError("Passwords do not match!");
            return;
        };

        if (!termsAccepted) {
            setError("Please accept the terms and conditions.");
            return;
        }

        if (!validatePhoneNumber(phoneNumber)) {
            setError("Invalid phone number format. Please enter a valid phone number.");
            return;
        }        

        if (!validatePostCode(postCode)) {
            setError("Invalid zip code. Must be 4–10 digits.");
            return;
        }
    
        if (!validateAddress(addressLine1)) {
            setError("Address is too short or invalid.");
            return;
        }

        const currentDate = new Date();
        const birthDate = new Date(dob);
        let age = currentDate.getFullYear() - birthDate.getFullYear();
        const m = currentDate.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
            age--;
        }

        if (age < 18) {
            setError("You must be at least 18 years old to sign up.");
            return;
        }

        if (!postCode || !addressLine1 || !firstName || !lastName || !phoneNumber || !dob) {
            setError("Please enter all required information")
            return;
        }

        try {
             // Create user with email and password
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Store user data in Firestore
            await setDoc(doc(db, "Users", user.uid), {
                uId: user.uid,
                email: user.email,
                AddressLine1: addressLine1,
                AddressLine2: '',
                DoB: dob,
                Postcode: postCode,
                isAdmin: false,
                Auctions: {},
                FirstName: firstName,
                LastName: lastName,
                PhoneNumber: phoneNumber,
                profilePicture: '',
                acceptCommunication: communication
            });

            setError(null);
            await fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/notifications/welcome-email", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  to: user.email
                }),
              });
            setCurrentView("login");

            navigate('/home');
        } catch (error) {
            const code = error?.code || (error?.message?.includes("auth/") ? error.message.match(/auth\/[a-zA-Z\-]+/)?.[0] : null);
            const message = getFriendlyError(code);
            setError(message);
        }
    };      

    const toggleTermsAccepted = () => {
        setTermsAccepted(!termsAccepted);
    };

    const toggleCommunication = () => {
        setCommunication(!communication);
    };
    
    return (
        <div className="loginContainer">
            <div className="loginDetails">
                <h1>BCA</h1>

                <div className="login" style={{ display: currentView === 'login' ? '' : 'none' }}>
                    <div className="loginContent">
                        <h2>Get Started</h2>
                        <p>Welcome to BCA - Login to get started for free</p>
                    </div>
                    <form onSubmit={handleLogin}>
                        <label>Email</label>
                        <input type="email" placeholder="example@gmail.com" onChange={(e) => setEmail(e.target.value)} required />
                        <label className="passwordLabel">
                            Password 
                            <div className="forgotPasswordContainer">
                                <button type="button" className="forgotPassword" onClick={handleForgotPassword}>
                                    Forgot Password?
                                </button>
                            </div>
                        </label>
                        <input type="password" placeholder="Password" onChange={(e) => setPassword(e.target.value)} required />
                        <div className="buttonContainer">
                            <button type="submit">Login</button>
                            <button className="switchViewBtn" onClick={handleSwitchView}>Don't have an account? <span className="boldText">Sign Up</span></button>
                        </div>
                    </form>

                    {error && (
                        <p style={{color: "red"}}>Error: {error}</p>
                    )}
                </div>
                <div className="forgotPassword" style={{ display: currentView === 'forgotPassword' ? '' : 'none' }}>
                    <div className="loginContent">
                        <h2>Reset Password</h2>
                        <p>Enter your email to receive a password reset link</p>
                    </div>
                    <form onSubmit={handleForgotPasswordSubmit}>
                        <label>Email</label>
                        <input type="email" placeholder="example@gmail.com" onChange={(e) => setEmail(e.target.value)} required />
                        <div className="buttonContainer">
                            <button type="submit" onClick={handleForgotPasswordSubmit}>Send Reset Link</button>
                            <button className="switchViewBtn" onClick={() => setCurrentView('login')}>Back to Login</button>
                        </div>
                    </form>

                    {error && (
                        <p style={{color: "red"}}>Error: {error}</p>
                    )}
                </div>
                <div className="signUp" style={{ display: currentView === 'signUp' ? '' : 'none' }}>
                    <div className="loginContent">
                        <h2>Get Started</h2>
                        <p>Welcome to BCA - Create a free account</p>
                    </div>
                    {stage === 1 ? (
                            <form>
                                <label>Email</label>
                                <input type="email" placeholder="example@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} required />

                                <label>Password</label>
                                <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />

                                <label>Confirm Password</label>
                                <input type="password" placeholder="Confirm Password" value={confPassword} onChange={(e) => setConfPassword(e.target.value)} required />

                                <label>Date of Birth</label>
                                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />

                                <div className="buttonContainer">
                                    <button className="button" type="button" onClick={handleNext}>Next</button>
                                    <button className="switchViewBtn" onClick={handleSwitchView}>
                                        Already have an account? <span className="boldText">Login</span>
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleSignUp} autoComplete='off'>
                                <label>First Name</label>
                                <input type="text" placeholder="John" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />

                                <label>Last Name</label>
                                <input type="text" placeholder="Doe" value={lastName} onChange={(e) => setLastName(e.target.value)} required />

                                <label>Phone Number</label>
                                <input type="text" placeholder="123-4567-8901" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
                                {!validatePhoneNumber(phoneNumber) && phoneNumber.length > 0 && (
                                    <p style={{ color: "red" }}>Invalid phone number</p>
                                )}

                                <label>Address Line 1</label>
                                <input type="text" placeholder="123 Place Road" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required />

                                <label>Zip Code</label>
                                <input type="text" placeholder="0123456" value={postCode} onChange={(e) => setPostCode(e.target.value)} required />

                                <label className="termsCheckBox">
                                    <input type="checkbox" onChange={toggleCommunication} />
                                    I consent to receive text messages from Big Circle Auctions. Messages sent may include customer care, account notifications, 
                                    marketing and message frequency will be varied based on the customer's interaction and preferences.
                                    Message and data rates may apply. For assistance Text "HELP" or Contact us at contact@bigcircleauctions.com for support. Text STOP to opt out of future messages at anytime.
                                </label>

                                <label className="termsCheckBox">
                                    <input type="checkbox" onChange={toggleTermsAccepted} /> I accept the <Link to="/terms-conditions" className="termsLink">Terms & Conditions</Link>
                                </label>

                                <div className="buttonContainer">
                                    <button type="submit">Sign Up</button>
                                    <button className="switchViewBtn" onClick={() => setStage(1)}>Back</button>
                                </div>
                            </form>
                        )}

                    {error && (
                        <p style={{color: "red"}}>Error: {error}</p>
                    )}
                </div>
            </div>
            <div className="loginCoverImage">

            </div>
        </div>
    )
}

export default Login